import { useCallback, useMemo, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ClientApiError, useApiConfig } from "@visioni/a3s-client-api/app";
import type { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import type { CancelCompilationResponse } from "../../client-messages/compilation";
import { cancelCompilationMutation } from "../queries";

export interface CancelCompilationParams {
  readonly siteId: SiteId;
  readonly assetCameraKey: AssetCameraKey;
  readonly requestId: string;
}

export interface CancelCompilationResult {
  /** Cancel a compilation still waiting in its camera's queue. */
  readonly cancelCompilation: (params: CancelCompilationParams) => Promise<CancelCompilationResponse>;
  /** The compilation a cancel is in flight for, if any. */
  readonly cancellingId: string | undefined;
}

/**
 * The server's explanation of a refused cancel: the job was sent to the device
 * in the meantime (409). Any other failure returns undefined.
 */
export function compilationCancelRefusal(error: unknown): string | undefined {
  return error instanceof ClientApiError && error.status === 409 ? error.message : undefined;
}

/**
 * Mutation hook for `POST /compilation/{requestId}/cancel`. Invalidates the
 * compilations list on settle, so the row shows as cancelled or, after a 409,
 * as sent.
 */
export function useCancelCompilation(): CancelCompilationResult {
  const apiConfig = useApiConfig();
  const queryClient = useQueryClient();

  const mutationConfig = useMemo(
    () =>
      apiConfig.tenant ?
        {
          tenantId: apiConfig.tenant.tenantId,
          apiBaseUrl: apiConfig.tenant.apiBaseUrl,
          authenticatedFetch: apiConfig.authenticatedFetch,
        }
      : { authenticatedFetch: apiConfig.authenticatedFetch },
    [apiConfig.tenant, apiConfig.authenticatedFetch],
  );

  const mutation = useMutation({
    mutationFn: ({ siteId, assetCameraKey, requestId }: CancelCompilationParams) =>
      cancelCompilationMutation(mutationConfig, { siteId, assetCameraKey, requestId }).mutationFn({}),
    // Returned so the cancel stays pending until the list has refetched, rather
    // than the row showing Cancel again in between.
    onSettled: () => queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "compilations" }),
  });

  const mutateAsyncRef = useRef(mutation.mutateAsync);
  mutateAsyncRef.current = mutation.mutateAsync;
  const cancelCompilation = useCallback((params: CancelCompilationParams) => mutateAsyncRef.current(params), []);

  return {
    cancelCompilation,
    cancellingId: mutation.isPending ? mutation.variables.requestId : undefined,
  };
}
